'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
import Link from 'next/link';
import { ArrowUpRight, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { VA_EVENTS } from '@/lib/analytics/event-catalog';
import { vaTrack } from '@/lib/analytics/vercel-events';
import Image from 'next/image';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-between gap-6 p-6 text-center bg-neutral-50 dark:bg-neutral-950 relative">
      <Image
        src="/logo/logo-symbol.svg"
        alt="Land Corp Group Logo"
        width={100}
        height={100}
        className="size-10 xl:size-12 shrink-0 mt-6"
      />
      <div className="flex flex-col items-center justify-center gap-6">
        <p className="font-heading text-7xl font-normal tracking-tight text-[#948B7F] dark:text-[#FCFAD7]">
          Error
        </p>
        <div className="max-w-md space-y-3">
          <h1 className="font-heading text-2xl tracking-tight text-foreground sm:text-3xl">
            Something went wrong
          </h1>
          <p className="text-sm xl:text-base text-muted-foreground font-light text-pretty">
            An unexpected error occurred. You can try again or go back to the homepage.
          </p>
          {error.digest ? (
            <p className="font-bold text-xs xl:text-sm text-muted-foreground">
              REF: {error.digest}
            </p>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-center gap-3">
          <Button
            variant="default"
            size="lg"
            onClick={() => {
              vaTrack(VA_EVENTS.ERROR_PAGE_RETRY);
              reset();
            }}
            className="bg-[#948B7F] text-white cursor-pointer rounded-full hover:bg-[#BFB7A1] hover:text-white transition-all duration-300 ease-in-out w-80 sm:w-52 px-4 h-10 font-semibold inline-flex items-center justify-center gap-2 group"
          >
            <RefreshCw
              className="size-4 shrink-0 group-hover:rotate-90 transition-all duration-300 ease-in-out"
              aria-hidden
            />
            Try again
          </Button>
          <Link href="/">
            <Button
              variant="default"
              size="lg"
              className="bg-[#948B7F] text-white cursor-pointer rounded-full hover:bg-[#BFB7A1] hover:text-white transition-all duration-300 ease-in-out w-80 sm:w-52 px-4 h-10 font-semibold inline-flex items-center justify-center gap-2 group"
            >
              Return to homepage
              <ArrowUpRight
                className="size-4 shrink-0 group-hover:rotate-45 transition-all duration-300 ease-in-out"
                aria-hidden
              />
            </Button>
          </Link>
        </div>
      </div>
      <p className="font-light text-[10px] sm:text-[11px] text-muted-foreground uppercase">
        Copyright &copy; {new Date().getFullYear()} Land Corp. Construction Group, INC. All rights
        reserved.
      </p>
    </div>
  );
}
